import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Modal, Select, Table, Tooltip, Spin } from 'antd';
import { EditOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import './styles.scss';
import { PoiModalNearbyPoiType } from '../../../types/poiModal';
import { LocationType } from '../../../common/api/spidertracks-sdk/types/PointOfInterest';
import { getDistanceUnit, getUserOrgData } from '../../../redux/selectors/userData';
import {
  getLoadingNearbyPois,
  getNearbyPois,
  getSelectedFlightlogEvent,
  getAllLocationTypes,
  getDraftForm
} from '../../../redux/selectors/poiModal';
import { resetPoiModal, setDraftForm } from '../../../redux/slice/poiModal/poiModal';
import { getInstance } from '../../../common/api/spidertracks-sdk';
import { abbreviateDistanceUnit, convertMetresTo } from '../../Flying/Map/utils/helper';
import {
  getLocationTypeDisplayName,
  getLocationTypeCodeById,
  POI_CODE_MAX_LENGTH,
  POI_NAME_MAX_LENGTH,
  getPoiCodeValidationErrorMessage,
  getPoiNameValidationErrorMessage
} from '../../../util/poi';
import { findFlightlogsEventPoiByEventId } from './utils/findFlightlogsEventPoiByEventId';
import {
  showErrorNotification,
  showSuccessNotification
} from '../../../helpers/globalNotifications';

const deduceAvailableLocationTypesFromAircraftType = (
  aircraftType: string | undefined,
  allLocationTypes: LocationType[]
) => {
  if (!aircraftType) {
    console.error('Aircraft type is not defined');
    return undefined;
  }

  const availableLocationTypes = allLocationTypes.filter(locationType => {
    if (aircraftType === 'ROTARY_WING') {
      return locationType.abbreviation === 'HLS';
    } else if (aircraftType === 'FIXED_WING') {
      return locationType.abbreviation === 'ALS' || locationType.abbreviation === 'HLS';
    } else {
      return locationType.rotaryWing === true && locationType.fixedWing === true;
    }
  });

  return availableLocationTypes.map(locationType => ({
    value: locationType.id,
    label: locationType.description,
    abbreviation: locationType.abbreviation
  }));
};

const PoiModal = ({ setPoiModalVisible }: { setPoiModalVisible: (visible: boolean) => void }) => {
  const [isCodeTouched, setIsCodeTouched] = useState(false);
  const [isNameTouched, setIsNameTouched] = useState(false);
  const [isTypeTouched, setIsTypeTouched] = useState(false);
  const dispatch = useDispatch();
  const SpidertracksSDK = getInstance();

  const selectedFlightlogEvent = useSelector(getSelectedFlightlogEvent);
  const initialSelectedPoi =
    selectedFlightlogEvent?.selectedEventId && selectedFlightlogEvent.track?.flightLog
      ? findFlightlogsEventPoiByEventId(
          selectedFlightlogEvent.selectedEventId,
          selectedFlightlogEvent.track.flightLog
        )
      : undefined;
  const initialSelectedPoiId = initialSelectedPoi?.locationId ?? undefined;

  const [selectedPoiId, setSelectedPoiId] = useState<number | undefined>(initialSelectedPoiId);
  const nearbyPois = useSelector(getNearbyPois);
  const loadingNearbyPois = useSelector(getLoadingNearbyPois);
  const allLocationTypes = useSelector(getAllLocationTypes);
  const newPoiLocationTypeOptions = deduceAvailableLocationTypesFromAircraftType(
    selectedFlightlogEvent?.track?.aircraft?.vehicleType,
    allLocationTypes
  );
  const draftForm = useSelector(getDraftForm);
  const userOrgData = useSelector(getUserOrgData);
  const orgPoiCreateMinimumRole = userOrgData?.find(
    org => org.org.id === selectedFlightlogEvent?.track?.orgId
  )?.org?.poiCreateMinimumRole;
  const userRoleInOrg = userOrgData?.find(
    org => org.org.id === selectedFlightlogEvent?.track?.orgId
  )?.role;
  const isUserAllowedToCreatePoi =
    orgPoiCreateMinimumRole === 'MEMBER' || userRoleInOrg === 'ADMIN' || userRoleInOrg === 'OWNER';

  const distanceUnit = useSelector(getDistanceUnit);

  const getCodeColumnTitle = () => {
    return (
      <Tooltip
        title={
          <div>
            <b>Preference:</b>
            <br />
            Custom &gt; ICAO &gt; IATA &gt; Other
            <br />
            Manage under Admin &gt; Locations
          </div>
        }
      >
        CODE
        <ExclamationCircleFilled className="ml-2" style={{ color: 'rgba(0,0,0,0.25)' }} /> (source)
      </Tooltip>
    );
  };

  const dispatchDraftFormChange = (draftFormField: string, value: string | number) => {
    dispatch(setDraftForm({ ...draftForm, [draftFormField]: value }));
  };

  const tableColumns = [
    {
      title: `DISTANCE (${abbreviateDistanceUnit(distanceUnit)})`,
      dataIndex: 'distance',
      key: 'distance',
      width: '6rem',
      render: (text: PoiModalNearbyPoiType['distance'], record: PoiModalNearbyPoiType) => {
        if (record.locationId === 0) {
          return <Input defaultValue="0.0" disabled />;
        }
        return convertMetresTo(text, distanceUnit)[0];
      }
    },
    {
      title: () => getCodeColumnTitle(),
      dataIndex: 'code',
      key: 'code',
      width: '6rem',
      render: (text: PoiModalNearbyPoiType['code'], record: PoiModalNearbyPoiType) => {
        if (record.locationId === 0) {
          return (
            <div style={{ position: 'relative' }}>
              <Tooltip title={`Up to ${POI_CODE_MAX_LENGTH} characters`}>
                <Input
                  placeholder="Add Code"
                  onBlur={() => setIsCodeTouched(true)}
                  onChange={e => {
                    setIsCodeTouched(true);
                    dispatchDraftFormChange('code', e.target.value.toUpperCase());
                  }}
                  className={`uppercase-input ${
                    (draftForm?.code && getPoiCodeValidationErrorMessage(draftForm.code)) ||
                    (isCodeTouched && getPoiCodeValidationErrorMessage(draftForm?.code || ''))
                      ? 'input-error-border'
                      : ''
                  }`}
                />
              </Tooltip>
              {isCodeTouched && getPoiCodeValidationErrorMessage(draftForm?.code || '') && (
                <p className="validation-error">
                  {getPoiCodeValidationErrorMessage(draftForm?.code || '')}
                </p>
              )}
            </div>
          );
        }
        return text;
      }
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      width: '20rem',
      render: (text: PoiModalNearbyPoiType['name'], record: PoiModalNearbyPoiType) => {
        if (record.locationId === 0) {
          return (
            <Tooltip title={`Up to ${POI_NAME_MAX_LENGTH} characters`}>
              <Input
                placeholder="Add Location Name"
                onBlur={() => setIsNameTouched(true)}
                onChange={e => {
                  setIsNameTouched(true);
                  dispatchDraftFormChange('name', e.target.value);
                }}
                className={
                  (draftForm?.name && getPoiNameValidationErrorMessage(draftForm.name)) ||
                  (isNameTouched && getPoiNameValidationErrorMessage(draftForm?.name || ''))
                    ? 'input-error-border'
                    : ''
                }
              />
              {isNameTouched && getPoiNameValidationErrorMessage(draftForm?.name || '') && (
                <p className="validation-error">
                  {getPoiNameValidationErrorMessage(draftForm?.name || '')}
                </p>
              )}
            </Tooltip>
          );
        }
        return text;
      }
    },
    {
      title: 'TYPE',
      dataIndex: 'type',
      key: 'type',
      width: '15rem',
      render: (text: PoiModalNearbyPoiType['locationTypeId'], record: PoiModalNearbyPoiType) => {
        if (record.locationId === 0) {
          return (
            <div>
              <Select
                style={{ width: '100%' }}
                placeholder="Select Type"
                onBlur={() => setIsTypeTouched(true)}
                onChange={(value: number) => {
                  setIsTypeTouched(true);
                  dispatchDraftFormChange('type', value);
                }}
                className={isTypeTouched && !draftForm?.type ? 'input-error-border' : ''}
              >
                {newPoiLocationTypeOptions?.map(option => {
                  const locationType = allLocationTypes.find(type => type.id === option.value);
                  return (
                    <Select.Option key={option.value} value={option.value}>
                      {locationType ? getLocationTypeDisplayName(locationType) : 'unknown'}
                    </Select.Option>
                  );
                })}
              </Select>
              {isTypeTouched && !draftForm?.type && (
                <p className="validation-error">Type is required</p>
              )}
            </div>
          );
        }
        const locationType = allLocationTypes.find(type => type.id === record.locationTypeId);
        return locationType ? getLocationTypeDisplayName(locationType) : 'unknown';
      }
    }
  ];

  const sharedRowSelection = {
    type: 'radio' as const,
    selectedRowKeys: [selectedPoiId ?? 0],
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedPoiId(Number(selectedRowKeys[0]));
    }
  };

  const isOkButtonDisabled =
    loadingNearbyPois ||
    !distanceUnit ||
    (selectedPoiId === 0
      ? getPoiCodeValidationErrorMessage(draftForm?.code || '') ||
        getPoiNameValidationErrorMessage(draftForm?.name || '') ||
        !draftForm?.type
      : false);

  const savePoiChanges = async () => {
    if (!selectedFlightlogEvent?.selectedEventId) {
      console.error(
        'Something went wrong when trying to save POI changes: No selected flightlog event id from redux'
      );
      showErrorNotification('Something went wrong, please try again later');
      return;
    }

    // Create new POI
    if (selectedPoiId === 0) {
      if (draftForm) {
        try {
          const locationTypeName = draftForm.type
            ? getLocationTypeCodeById(draftForm.type, allLocationTypes)
            : undefined;

          const response = await SpidertracksSDK.getPoiService().createCustomPois(
            selectedFlightlogEvent.track?.orgId,
            draftForm.latitude,
            draftForm.longitude,
            locationTypeName,
            draftForm.code?.toUpperCase(),
            draftForm.name
          );
          const createdPoiId = response?.locationId;
          if (!createdPoiId) {
            throw new Error('Created custom POI does not have a locationId');
          }
          await SpidertracksSDK.getPoiService().updateFlightlogEventPoi(
            selectedFlightlogEvent.selectedEventId,
            createdPoiId
          );
          showSuccessNotification('Point of interest created');
        } catch (error) {
          console.error(error);
          showErrorNotification('Unable to create point of interest for this location');
        }
      }
      // Update existing POI
    } else {
      if (selectedPoiId == null || selectedFlightlogEvent.selectedEventId == null) {
        console.error('Missing required fields to update flightlog event poi');
        showErrorNotification('Something went wrong, please try again later');
        return;
      }
      try {
        await SpidertracksSDK.getPoiService().updateFlightlogEventPoi(
          selectedFlightlogEvent.selectedEventId,
          selectedPoiId
        );
        showSuccessNotification('Point of interest for this location updated successfully');
      } catch (error) {
        console.error(error);
        showErrorNotification('Unable to update point of interest for this location');
      }
    }
    setPoiModalVisible(false);
    dispatch(resetPoiModal());
  };

  return (
    <Modal
      className="poi-modal"
      width="58rem"
      title={
        <div>
          <EditOutlined />
          <span> Update or Create Location</span>
        </div>
      }
      okText="Save"
      style={{ border: 0 }}
      maskStyle={{ border: 0 }}
      bodyStyle={{ border: 0 }}
      visible={true}
      closable={false}
      destroyOnClose={true}
      onCancel={() => {
        setPoiModalVisible(false);
        dispatch(resetPoiModal());
      }}
      onOk={() => savePoiChanges()}
      okButtonProps={isOkButtonDisabled ? { disabled: true } : { disabled: false }}
    >
      <Spin spinning={!distanceUnit} />
      {distanceUnit !== undefined && (
        <div className="poi-modal-content">
          <section>
            <div style={{ paddingBottom: '0.5rem' }}>Select existing:</div>
            <Table
              columns={tableColumns}
              dataSource={nearbyPois || []}
              pagination={false}
              bordered
              rowKey="locationId"
              rowSelection={sharedRowSelection}
              loading={loadingNearbyPois}
              onRow={record => ({
                onClick: () => {
                  setSelectedPoiId(Number(record.locationId));
                }
              })}
            />
          </section>
          {!loadingNearbyPois && isUserAllowedToCreatePoi && (
            <section>
              <div style={{ paddingBottom: '0.5rem' }}>
                Or add new:
                <Tooltip
                  title={
                    <div>
                      Enable for all users under{' '}
                      <span style={{ whiteSpace: 'nowrap' }}>Admin &gt; Locations</span>, otherwise
                      Admin only
                    </div>
                  }
                  style={{ display: 'inline-block' }}
                >
                  <ExclamationCircleFilled className="ml-2" style={{ color: 'rgba(0,0,0,0.25)' }} />
                </Tooltip>
              </div>
              <Table
                className="new-poi-table"
                columns={tableColumns}
                dataSource={[
                  {
                    locationId: 0,
                    name: '',
                    code: '',
                    distance: 0,
                    locationTypeId: undefined,
                    latitude: 0,
                    longitude: 0
                  }
                ]}
                pagination={false}
                showHeader={false}
                rowKey="locationId"
                rowSelection={sharedRowSelection}
                onRow={record => {
                  return {
                    onClick: () => {
                      setSelectedPoiId(Number(record.locationId));
                    }
                  };
                }}
              />
            </section>
          )}
        </div>
      )}
    </Modal>
  );
};

PoiModal.displayName = 'PoiModal';

export default PoiModal;
